import React, { useState } from "react";
import { Maximize2 } from "lucide-react";
import { fileUrl } from "../lib";
import { SBMidiaViewer } from "./SBMidiaViewer";

const TZ = "America/Sao_Paulo";
const diaKey = (iso) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;  // data_captura (só data)
  try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ }); } catch { return ""; }
};
const diaLabel = (key) => {
  if (!key) return "Sem data";
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("pt-BR", { timeZone: "UTC", day: "2-digit", month: "long", year: "numeric" });
};

/* Junta fotos aprovadas, fotos de séries aprovadas e vídeos do SB, sem URLs repetidas. */
function montarItens(fotos, series, videos) {
  const vistos = new Set();
  const out = [];
  const add = (x, kind, extra = {}) => {
    if (!x?.url || vistos.has(x.url)) return;
    vistos.add(x.url);
    const data = x.data_captura || x.uploaded_at || x.created_at || extra.data || null;
    out.push({ id: x.id || x.url, url: x.url, kind, data, dia: data ? diaKey(data) : "", legenda: x.legenda || "", etapa: x.etapa || extra.etapa || "", thumb: x.thumbnail_url });
  };
  (fotos || []).filter(f => f.aprovada !== false).forEach(f => add(f, "foto"));
  (series || []).forEach(s => (s.fotos || []).forEach(f => add(f, "foto", { etapa: s.etapa_nome })));
  (videos || []).forEach(v => add(v, "video"));
  return out.sort((a, b) => (b.data || "").localeCompare(a.data || ""));
}

function ItemMidia({ it, onOpen }) {
  const hora = it.data && it.data.length > 10 ? new Date(it.data).toLocaleTimeString("pt-BR", { timeZone: TZ, hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <figure style={{ margin: 0, pageBreakInside: "avoid" }} data-testid={`sb-timeline-item-${it.id}`}>
      <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden", border: "1px solid var(--gold-3)", background: "#0a0a0c" }}>
        {it.kind === "video" ? (
          <video src={`${fileUrl(it.url)}#t=0.1`} poster={it.thumb ? fileUrl(it.thumb) : undefined} controls playsInline preload="metadata"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} data-testid={`sb-timeline-video-${it.id}`} />
        ) : (
          <img src={fileUrl(it.url)} alt={it.legenda || it.etapa} loading="lazy" onClick={() => onOpen(it)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in", display: "block" }} />
        )}
        <span className="sb-timeline-date" style={it.kind === "video" ? { top: 8, bottom: "auto" } : undefined} data-testid={`sb-timeline-date-${it.id}`}>
          {it.dia ? diaLabel(it.dia).replace(/ de /g, " ") : "Sem data"}{hora ? ` · ${hora}` : ""}
        </span>
        {it.kind === "video" && (
          <button type="button" className="sb-timeline-kind" onClick={() => onOpen(it)} data-testid={`sb-timeline-expand-${it.id}`}
            style={{ pointerEvents: "auto", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
            Vídeo <Maximize2 size={10} />
          </button>
        )}
      </div>
      {(it.legenda || it.etapa) && (
        <figcaption style={{ padding: "8px 2px 0", fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 13, color: "var(--ink-2)" }}>
          {it.legenda || it.etapa}
          {it.legenda && it.etapa && <span style={{ display: "block", fontFamily: "inherit", fontStyle: "normal", fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: "var(--gold)", marginTop: 3 }}>{it.etapa}</span>}
        </figcaption>
      )}
    </figure>
  );
}

/* Cronologia de fotos e vídeos — do mais recente ao mais antigo, agrupada por dia. */
export function SBCronologiaMidia({ fotos, series, videos }) {
  const [aberto, setAberto] = useState(null);
  const itens = montarItens(fotos, series, videos);
  if (itens.length === 0) return null;
  const onOpen = (it) => setAberto(itens.indexOf(it));
  const dias = [];
  itens.forEach(it => {
    const ult = dias[dias.length - 1];
    if (ult && ult.dia === it.dia) ult.itens.push(it); else dias.push({ dia: it.dia, itens: [it] });
  });
  const nVid = itens.filter(i => i.kind === "video").length;
  return (
    <div className="sb-section" data-testid="sb-timeline">
      <div className="sb-h">
        <div className="sb-h-title">Cronologia da obra</div>
        <div className="sb-h-code">{itens.length - nVid} foto(s){nVid ? ` · ${nVid} vídeo(s)` : ""}</div>
      </div>
      <div className="sb-timeline">
        {dias.map(d => (
          <div key={d.dia || "sem-data"} className="sb-timeline-day" data-testid={`sb-timeline-day-${d.dia || "sem-data"}`}>
            <div className="sb-timeline-day-label">{diaLabel(d.dia)}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,220px),1fr))", gap: 14 }}>
              {d.itens.map(it => <ItemMidia key={it.id} it={it} onOpen={onOpen} />)}
            </div>
          </div>
        ))}
      </div>
      {aberto !== null && <SBMidiaViewer itens={itens} index={aberto} onIndex={setAberto} onClose={() => setAberto(null)} />}
    </div>
  );
}

/* Bandeira italiana em destaque + lema, usada na capa do SB. */
export const SBFlag = () => (
  <div className="sb-flag-wrap" data-testid="sb-flag">
    <div className="sb-flag"><span className="v" /><span className="b" /><span className="r" /></div>
    <div className="sb-flag-motto" data-testid="sb-flag-motto">Passione italiana, progetti straordinari</div>
  </div>
);
